import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import VideoPlayer from '../components/VideoPlayer';

const SectionLearningPage = () => {
  const { id, sectionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);

  useEffect(() => {
    loadSectionWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sectionId]);

  useEffect(() => {
    const handlePopState = () => {
      navigate(`/dashboard/courses/${id}`, { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate, id]);

  const loadSectionWorkspace = async () => {
    setLoading(true);
    setError('');
    try {
      const courseId = parseInt(id, 10);
      const targetSectionId = parseInt(sectionId, 10);
      if (Number.isNaN(courseId) || Number.isNaN(targetSectionId)) {
        setError('Invalid course or section.');
        setLoading(false);
        return;
      }

      const [enrollmentsRes, sectionsRes] = await Promise.all([
        api.get('/api/user-dashboard/courses'),
        api.get(`/api/course-sections/course/${courseId}`)
      ]);

      const enrolledCourses = enrollmentsRes?.data?.enrollments || [];
      const isEnrolled = enrolledCourses.some((enrollment) => enrollment?.course?.id === courseId);
      if (!isEnrolled) {
        setError('You are not enrolled in this course.');
        setLoading(false);
        return;
      }

      const currentSection = (sectionsRes.data || []).find((section) => section.id === targetSectionId);
      if (!currentSection) {
        setError('Section not found.');
        setLoading(false);
        return;
      }
      setActiveSection(currentSection);

      const [contentsRes, questionsRes, liveSessionRes] = await Promise.allSettled([
        api.get(`/api/course-contents/section/${targetSectionId}`),
        api.get(`/api/course-questions/section/${targetSectionId}`),
        api.get(`/api/live-sessions/section/${targetSectionId}`)
      ]);

      const contents = contentsRes.status === 'fulfilled' ? (contentsRes.value.data || []) : [];
      const questions = questionsRes.status === 'fulfilled' ? (questionsRes.value.data || []) : [];
      const liveSession = liveSessionRes.status === 'fulfilled' ? liveSessionRes.value.data : null;

      const builtItems = [];
      contents.forEach((content) => {
        if (content.contentType === 'VIDEO') {
          builtItems.push({
            id: `video-${content.id}`,
            type: 'VIDEO',
            title: content.title,
            description: content.description || '',
            order: content.contentOrder || 9999,
            videoUrl: content.videoUrl,
            source: 'Course Video'
          });
        } else if (content.contentType === 'DOCUMENT') {
          builtItems.push({
            id: `doc-${content.id}`,
            type: 'NOTES',
            title: content.title,
            description: content.description || '',
            order: content.contentOrder || 9999,
            documentUrl: content.documentUrl,
            documentType: (content.documentType || '').toLowerCase()
          });
        } else if (content.contentType === 'QUESTION' && content.question) {
          builtItems.push({
            id: `content-question-${content.id}`,
            type: 'QUESTION',
            title: content.question.title || content.title,
            description: content.question.description || content.description || '',
            order: content.contentOrder || 9999,
            questionId: content.question.id,
            difficultyLevel: content.question.difficultyLevel || '',
            timeLimitSeconds: content.question.timeLimitSeconds,
            memoryLimitMB: content.question.memoryLimitMB,
            constraints: content.question.constraints || '',
            examples: content.question.examples || '',
            problemStatement: content.question.problemStatement || ''
          });
        }
      });

      questions.forEach((question) => {
        builtItems.push({
          id: `section-question-${question.id}`,
          type: 'QUESTION',
          title: question.title,
          description: question.description || '',
          order: question.questionOrder || 9999,
          questionId: question.id,
          difficultyLevel: question.difficultyLevel || '',
          timeLimitSeconds: question.timeLimitSeconds,
          memoryLimitMB: question.memoryLimitMB,
          constraints: question.constraints || '',
          examples: question.examples || '',
          problemStatement: question.problemStatement || ''
        });
      });

      if (liveSession?.recordingUrl) {
        builtItems.push({
          id: `live-recording-${liveSession.id}`,
          type: 'VIDEO',
          title: liveSession.title || 'Live Session Recording',
          description: liveSession.description || '',
          order: 0,
          videoUrl: liveSession.recordingUrl,
          source: 'Live Session Recording'
        });
      }

      builtItems.sort((a, b) => a.order - b.order);
      setItems(builtItems);
      const selectedFromQuery = new URLSearchParams(location.search).get('item');
      const itemExists = builtItems.some((item) => item.id === selectedFromQuery);
      const initialItemId = itemExists ? selectedFromQuery : (builtItems[0]?.id || null);
      setSelectedItemId(initialItemId); // auto-open first content (or query-selected)

      if (initialItemId) {
        navigate(`${location.pathname}?item=${encodeURIComponent(initialItemId)}`, { replace: true });
      }
    } catch (e) {
      console.error('Section workspace error:', e);
      setError('Failed to load section workspace.');
    }
    setLoading(false);
  };

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) || null,
    [items, selectedItemId]
  );

  const typeChip = (type) => {
    if (type === 'VIDEO') return 'bg-pink-500/20 text-pink-200 border-pink-400/30';
    if (type === 'NOTES') return 'bg-blue-500/20 text-blue-200 border-blue-400/30';
    return 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30';
  };

  const handleSelectItem = (itemId) => {
    setSelectedItemId(itemId);
    navigate(`${location.pathname}?item=${encodeURIComponent(itemId)}`, { replace: true });
  };

  const isPdfDocument = (item) => {
    if (!item || !item.documentUrl) return false;
    const url = item.documentUrl.toLowerCase();
    return item.documentType === 'pdf' || url.endsWith('.pdf') || url.includes('.pdf?');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 pt-20 pb-6">
      <div className="max-w-7xl mx-auto px-4">
        {loading && <div className="text-white/70">Loading section workspace...</div>}

        {!loading && error && <div className="text-red-300">{error}</div>}

        {!loading && !error && (
          <>
            <div className="mb-3 rounded-2xl p-[1px] bg-gradient-to-r from-white/15 via-indigo-300/25 to-cyan-300/20">
              <div className="bg-slate-900/90 border border-white/10 rounded-2xl p-3.5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="text-left">
                    <h2 className="text-white text-lg font-semibold">{activeSection?.title}</h2>
                    {activeSection?.description && <p className="text-white/65 text-xs mt-0.5">{activeSection.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 sm:justify-end">
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-indigo-500/20 border border-indigo-300/30 text-indigo-100 whitespace-nowrap">
                      Focused Section
                    </span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-white/10 border border-white/20 text-white/75 whitespace-nowrap">
                      {items.length} items
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-4 bg-white/5 border border-white/10 rounded-2xl p-3 max-h-[72vh] overflow-y-auto">
                <p className="text-white font-semibold px-2 py-2">Content List</p>
                {items.length === 0 ? (
                  <p className="text-white/60 text-sm px-2">No content in this section.</p>
                ) : (
                  <div className="space-y-2">
                    {items.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => handleSelectItem(item.id)}
                        className={`w-full text-left rounded-xl p-3 border transition ${
                          selectedItemId === item.id
                            ? 'bg-white/15 border-white/40'
                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-white text-sm font-medium line-clamp-1">{index + 1}. {item.title}</span>
                          <span className={`px-2 py-0.5 text-[10px] border rounded-full ${typeChip(item.type)}`}>
                            {item.type}
                          </span>
                        </div>
                        {item.description && <p className="text-white/60 text-xs line-clamp-2">{item.description}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={`lg:col-span-8 bg-white/5 border border-white/10 rounded-2xl min-h-[72vh] ${(selectedItem?.type === 'VIDEO' || selectedItem?.type === 'NOTES') ? 'p-0 overflow-hidden' : 'p-4'}`}>
                {!selectedItem ? (
                  <div className="text-white/60 p-4">Select any content from the left panel.</div>
                ) : (
                  <div>
                    {selectedItem.type === 'VIDEO' && selectedItem.videoUrl && (
                      <VideoPlayer
                        videoUrl={selectedItem.videoUrl}
                        title=""
                        description=""
                        isUploaded={true}
                        fullWidth={true}
                      />
                    )}

                    {selectedItem.type === 'NOTES' && selectedItem.documentUrl && (
                      <div className="w-full h-[72vh] bg-black">
                        {isPdfDocument(selectedItem) ? (
                          <object
                            data={selectedItem.documentUrl}
                            type="application/pdf"
                            className="w-full h-full"
                          >
                            <iframe
                              src={selectedItem.documentUrl}
                              title="PDF Viewer"
                              className="w-full h-full border-0"
                              loading="lazy"
                            />
                          </object>
                        ) : (
                          <iframe
                            src={selectedItem.documentUrl}
                            title="Document Viewer"
                            className="w-full h-full border-0"
                            loading="lazy"
                          />
                        )}
                      </div>
                    )}

                    {selectedItem.type === 'QUESTION' && selectedItem.questionId && (
                      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 sm:p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 text-xs border rounded-full ${typeChip(selectedItem.type)}`}>
                            {selectedItem.type}
                          </span>
                          <h3 className="text-white text-lg font-semibold">{selectedItem.title}</h3>
                        </div>
                        {selectedItem.description && <p className="text-white/75 text-sm mb-4 leading-relaxed">{selectedItem.description}</p>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          {selectedItem.difficultyLevel && (
                            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-300/20 rounded-xl p-3">
                              <p className="text-amber-200/70 text-xs mb-1 uppercase tracking-wide">Difficulty</p>
                              <p className="text-amber-100 text-sm font-semibold">{selectedItem.difficultyLevel}</p>
                            </div>
                          )}
                          {(selectedItem.timeLimitSeconds || selectedItem.memoryLimitMB) && (
                            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-300/20 rounded-xl p-3">
                              <p className="text-cyan-200/70 text-xs mb-1 uppercase tracking-wide">Limits</p>
                              <p className="text-cyan-100 text-sm font-semibold">
                                {selectedItem.timeLimitSeconds ? `${selectedItem.timeLimitSeconds}s` : '-'}
                                {' • '}
                                {selectedItem.memoryLimitMB ? `${selectedItem.memoryLimitMB}MB` : '-'}
                              </p>
                            </div>
                          )}
                        </div>

                        {selectedItem.problemStatement && (
                          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 mb-3 text-left">
                            <p className="text-white/60 text-xs mb-1 uppercase tracking-wide">Problem Statement</p>
                            <p className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{selectedItem.problemStatement}</p>
                          </div>
                        )}

                        {selectedItem.constraints && (
                          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 mb-3 text-left">
                            <p className="text-white/60 text-xs mb-1 uppercase tracking-wide">Constraints</p>
                            <p className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{selectedItem.constraints}</p>
                          </div>
                        )}

                        {selectedItem.examples && (
                          <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 mb-5 text-left">
                            <p className="text-white/60 text-xs mb-1 uppercase tracking-wide">Examples</p>
                            <p className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{selectedItem.examples}</p>
                          </div>
                        )}

                        <a
                          href={`/question-ide?questionId=${selectedItem.questionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-medium shadow-lg shadow-emerald-900/30 transition-all"
                        >
                          Open in IDE
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <footer className="mt-6 mb-2 border-t border-white/10 pt-4 text-center">
              <p className="text-white/50 text-xs">Focus mode on. One section at a time.</p>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default SectionLearningPage;
